import { canReadProperties } from '@/api/core/authorization';
import { PROPERTY_PAGE_LIMIT } from '@/api/core/properties';
import { useOperatorSession } from '@/auth/runtime';
import { SearchField } from '@/components/search-field';
import { isUuid } from '@/lib/validation';
import { propertiesQuery, type PropertyRuntime } from '@/pages/property/property-queries';
import { OptionSelect } from '@/pages/settings/option-select';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export interface PropertyDirectory {
  /** property_id → 지점명. 지점 조회 권한이 없거나 아직 불러오지 못했으면 null. */
  names: ReadonlyMap<string, string> | null;
  /** 서버가 지점을 한 페이지에 모두 돌려줬는지. 아니면 목록으로 고르게 할 수 없다. */
  complete: boolean;
}

/**
 * 지점 이름 조회. Booking 권한만 있는 운영자는 지점 목록을 읽을 수 없으므로 그때는 UUID만 다룬다.
 * 서버가 has_more를 주지 않아, 첫 페이지가 한도보다 적게 오면 전체라고 본다.
 */
export function usePropertyDirectory(runtime: PropertyRuntime): PropertyDirectory {
  const session = useOperatorSession(runtime.session);
  const allowed =
    session.status === 'authorized' &&
    !!session.authorization &&
    !session.deniedDomains.includes('property') &&
    canReadProperties(session.authorization);
  const query = useQuery(propertiesQuery(runtime, session.generation, null, allowed));
  return useMemo(() => {
    if (!allowed || !query.data) return { names: null, complete: false };
    return {
      names: new Map(query.data.items.map((item) => [item.property_id, item.name])),
      complete: query.data.items.length < PROPERTY_PAGE_LIMIT,
    };
  }, [allowed, query.data]);
}

/**
 * 지점 필터 — 지점 목록을 다 알면 이름으로 고르는 셀렉트, 아니면 UUID를 붙여 넣고 Enter로 확정한다.
 * 확정 전 입력은 URL에 반영하지 않으므로 형식이 틀린 UUID는 onInvalid로만 알린다.
 */
export function PropertyFilterField({
  value,
  onChange,
  onInvalid,
  directory,
}: {
  value: string;
  onChange: (value: string) => void;
  onInvalid: (message: string) => void;
  directory: PropertyDirectory;
}) {
  const { names, complete } = directory;
  if (names && complete) {
    const options = [
      { value: '', label: '전체 지점' },
      ...[...names]
        .sort((a, b) => a[1].localeCompare(b[1], 'ko'))
        .map(([id, name]) => ({ value: id, label: name })),
    ];
    // 주소로 들어온 필터가 목록에 없는 지점이면 그 UUID를 그대로 보여 준다.
    if (value && !names.has(value)) options.push({ value, label: value });
    return (
      <OptionSelect
        value={value}
        options={options}
        onValueChange={onChange}
        aria-label="지점"
        size="sm"
        className="w-48"
      />
    );
  }
  return (
    <SearchField
      value={value}
      onSearch={(next) => {
        if (next && !isUuid(next)) {
          onInvalid('지점 ID 형식이 올바르지 않습니다.');
          return;
        }
        onChange(next);
      }}
      placeholder="지점 ID 입력 후 Enter"
      aria-label="지점 ID"
    />
  );
}
